import { useNavigate } from 'react-router-dom';
import { Avatar } from '@mui/material';
import { ArrowBackIos } from '@mui/icons-material';
import { getMetaData } from '../../../../tool';
import { Comment } from '../../../../types';
import useAnswerPageStyles from '../useAnswerPageStyles';
import VoteComponent from '../../voteComponent';
import CommentSection from '../../commentSection';
import useAnswerPage from '../../../../hooks/useAnswerPage';
import useGlobalStyles from '../../../useGlobalStyles';
import useLayoutStyles from '../../../layout/useLayoutStyles';
import AwardComponent from '../../baseComponents/award';
import useQuestionStyles from '../../questionPage/question/useQuestionStyles';
import { useAppSelector } from '../../../../redux/hooks';
import usePageLink from '../../../../hooks/useQuestion';
import { currentUserSelector, ulistSelector } from '../../../../redux/slices/userSlice';
import LoadingScreen from '../../../loadingScreen';

/**
 * AnswerHeader component that displays the header content of a question along with its answers.
 * It also includes the functionality to vote, ask a new question, and post a new answer.
 */
const AnswerHeader = () => {
  const { classes } = useAnswerPageStyles();
  const { classes: questionClasses } = useQuestionStyles();
  const { classes: layoutClasses } = useLayoutStyles();
  const { classes: globalClasses } = useGlobalStyles();

  const { question, questionID, handleNewComment } = useAnswerPage();
  const users = useAppSelector(ulistSelector);
  const currentUser = useAppSelector(currentUserSelector);
  const questionAuthor =
    question?.askedByUser && users?.find(user => user.username === question?.askedByUser.username);
  const { handleUser } = usePageLink();
  const navigate = useNavigate();

  if (!question || question?._id !== questionID) {
    return <LoadingScreen />;
  }

  return (
    <div className={classes.questionSection}>
      <div className={classes.backContainer} onClick={() => navigate('/questions')}>
        <ArrowBackIos />
        <span className={layoutClasses.boldTitle}>All Questions</span>
      </div>
      <div className={classes.questionInfo}>
        <div className={classes.awardViewsContainer}>
          <span className={layoutClasses.boldCount}>
            {question.views.length} {question.views.length === 1 ? 'view' : 'views'}
          </span>
          <AwardComponent
            awardReceiver={question.askedByUser}
            disabled={questionAuthor === null || questionAuthor?.username === currentUser?.username}
          />
        </div>
        <div className={classes.answerQuestionRow}>
          <div className={classes.answerQuestionContainer}>
            <span className={`${layoutClasses.boldTitle} ${classes.answerQuestionTitle}`}>
              {question.title}
            </span>
            <span className={classes.answerQuestionText}>{question.text}</span>
          </div>
        </div>
        <div className={classes.voteUserContainer}>
          <div
            className={classes.answerQuestionRight}
            onClick={() => {
              if (questionAuthor) handleUser(questionAuthor.username);
            }}>
            <Avatar
              className={globalClasses.userAvatar}
              src={`${process.env.PUBLIC_URL}/assets/profiles/${questionAuthor?.profilePicture}`}
            />
            <div className={classes.answerQuestionInfo}>
              <span
                className={`${questionClasses.questionAuthor} ${!questionAuthor && questionClasses.deletedQuestionAuthor}`}>
                {questionAuthor?.username || 'Deleted User'}
              </span>
              <span className={classes.answerQuestionMeta}>
                {getMetaData(new Date(question.askDateTime))}
              </span>
            </div>
          </div>
          <VoteComponent question={question} />
        </div>
      </div>
      <div className={classes.commentSection}>
        <CommentSection
          comments={question.comments}
          handleAddComment={(comment: Comment) => handleNewComment(comment, 'question', questionID)}
          width='100%'
        />
      </div>
    </div>
  );
};

export default AnswerHeader;
